import { translate, useLocale } from "../../localization";
import React, { useEffect, useRef } from "react";
import { Box } from "@mui/material";

const TURNSTILE_SCRIPT_ID = "cloudflare-turnstile-script";
const TURNSTILE_SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let turnstileScriptPromise = null;

const loadTurnstileScript = () => {
  if (typeof window === "undefined") {
    return Promise.reject(new Error(translate("Turnstile requires a browser environment.")));
  }

  if (window.turnstile) {
    return Promise.resolve(window.turnstile);
  }

  if (turnstileScriptPromise) {
    return turnstileScriptPromise;
  }

  turnstileScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID);
    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(window.turnstile), {
        once: true,
      });
      existingScript.addEventListener(
        "error",
        () => reject(new Error(translate("Unable to load Turnstile."))),
        { once: true }
      );
      return;
    }

    const script = document.createElement("script");
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.turnstile);
    script.onerror = () => reject(new Error(translate("Unable to load Turnstile.")));
    document.head.appendChild(script);
  });

  return turnstileScriptPromise;
};

const TurnstileWidget = ({
  siteKey,
  resetTrigger = 0,
  onTokenChange,
  onErrorChange,
}) => {
  const locale = useLocale();
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    if (!siteKey) {
      onTokenChange("");
      onErrorChange("");
      return undefined;
    }

    let isMounted = true;

    loadTurnstileScript()
      .then(() => {
        if (
          !isMounted ||
          !containerRef.current ||
          !window.turnstile ||
          widgetIdRef.current !== null
        ) {
          return;
        }

        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action: "signup",
          theme: "auto",
          language: locale === "es-419" ? "es" : locale === "pt-BR" ? "pt-br" : locale,
          callback: (token) => {
            if (!isMounted) {
              return;
            }
            onTokenChange(token);
            onErrorChange("");
          },
          "expired-callback": () => {
            if (!isMounted) {
              return;
            }
            onTokenChange("");
            onErrorChange(translate("Security check expired. Please try again."));
          },
          "error-callback": () => {
            if (!isMounted) {
              return;
            }
            onTokenChange("");
            onErrorChange(
              translate("Security check failed to load. Please refresh and try again.")
            );
          },
        });
      })
      .catch(() => {
        if (!isMounted) {
          return;
        }
        onTokenChange("");
        onErrorChange(
          translate("Security check failed to load. Please refresh and try again.")
        );
      });

    return () => {
      isMounted = false;
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, onTokenChange, onErrorChange, locale]);

  useEffect(() => {
    if (!siteKey || !window.turnstile || widgetIdRef.current === null) {
      return;
    }

    window.turnstile.reset(widgetIdRef.current);
    onTokenChange("");
  }, [resetTrigger, siteKey, onTokenChange]);

  if (!siteKey) {
    return null;
  }

  return (
    <Box sx={{ mt: 2, minHeight: 70, display: "flex", justifyContent: "center" }}>
      <div ref={containerRef} />
    </Box>
  );
};

export default TurnstileWidget;
